/*
 * Sticky mobile Add to cart bar.
 *
 * Shows a compact add-to-cart bar while the real submit button is out of view
 * on small screens. The bar does not own any cart logic: it proxies clicks to
 * the real button and mirrors its state, so Dawn's <product-form> keeps
 * handling variants, quantity, validation and the cart drawer.
 *
 * Visibility uses an explicit dead zone rather than a single IntersectionObserver
 * threshold. With one threshold the bar flips state every time the boundary is
 * crossed, so slow scrolling near the real button — and the viewport resize a
 * mobile browser fires when its address bar hides — made the bar flicker.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      // The real button must be this far inside the viewport before the bar
      // hides, and fully outside before it shows. Between the two the current
      // state is kept, which is what stops the flicker.
      static HIDE_MARGIN = 48;

      constructor() {
        super();
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.onMediaChange = this.onMediaChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.requestUpdate = this.requestUpdate.bind(this);
      }

      connectedCallback() {
        // A fixed element is positioned against the nearest ancestor with a
        // transform, filter or containment rather than the viewport. The bar is
        // rendered inside the product section, so hoist it to <body> and stop
        // depending on what the section's markup happens to do. Re-appending
        // re-enters this callback, hence the early return.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.target = document.querySelector(this.dataset.target);
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.priceOutput = this.querySelector('[data-sticky-atc-price]');
        this.priceSource = this.dataset.priceSource
          ? document.querySelector(this.dataset.priceSource)
          : null;

        if (!this.target || !this.button) return;

        this.button.addEventListener('click', this.onClick);
        this.mediaQuery.addEventListener('change', this.onMediaChange);
        this.onMediaChange();
      }

      disconnectedCallback() {
        this.mediaQuery.removeEventListener('change', this.onMediaChange);
        this.stopWatching();
      }

      onMediaChange() {
        if (this.mediaQuery.matches) {
          this.startWatching();
        } else {
          this.stopWatching();
          this.setVisible(false);
        }
      }

      startWatching() {
        if (this.watching) return;
        this.watching = true;

        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate, { passive: true });

        // Dawn swaps the button's disabled state and label when the variant
        // changes; mirror both rather than re-deriving availability here.
        this.stateObserver = new MutationObserver(() => this.syncState());
        this.stateObserver.observe(this.target, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.priceSource) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.syncState();
        this.syncPrice();
        this.update();
      }

      stopWatching() {
        if (!this.watching) return;
        this.watching = false;

        window.removeEventListener('scroll', this.requestUpdate);
        window.removeEventListener('resize', this.requestUpdate);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;

        [this.stateObserver, this.priceObserver].forEach((observer) => {
          if (observer) observer.disconnect();
        });
        this.stateObserver = this.priceObserver = null;
      }

      requestUpdate() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        const margin = StickyAddToCart.HIDE_MARGIN;
        const rect = this.target.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const comfortablyVisible = rect.top >= margin && rect.bottom <= viewportHeight - margin;
        const fullyOutOfView = rect.bottom <= 0 || rect.top >= viewportHeight;

        if (comfortablyVisible) {
          this.setVisible(false);
        } else if (fullyOutOfView) {
          this.setVisible(true);
        }
        // Anything in between is the dead zone: keep whatever state we are in.
      }

      syncState() {
        this.button.disabled = this.target.hasAttribute('disabled');

        // The real button holds its label in a direct child span, followed by
        // the loading spinner; :scope keeps us off the spinner's markup.
        const label = this.target.querySelector(':scope > span');
        if (label) {
          const text = label.textContent.trim();
          if (text) this.button.querySelector('span').textContent = text;
        }
      }

      syncPrice() {
        if (!this.priceSource || !this.priceOutput) return;

        // The price block renders both a regular and a sale variant and hides
        // one with display:none, alongside visually-hidden labels and Sale /
        // Sold out badges. Reading textContent picks up all of it, so take the
        // one price item that is actually laid out, preferring the sale price.
        const isRendered = (el) => el.offsetParent !== null && el.textContent.trim();
        const item =
          [...this.priceSource.querySelectorAll('.price-item--sale')].find(isRendered) ||
          [...this.priceSource.querySelectorAll('.price-item--regular')].find(isRendered);

        if (item) this.priceOutput.textContent = item.textContent.replace(/\s+/g, ' ').trim();
      }

      // Shopify's theme preview pins its own bar to the bottom of the viewport,
      // which would sit on top of this one. It only exists while previewing an
      // unpublished theme, so in production the offset resolves to zero.
      updateOffset() {
        const previewBar = document.getElementById('PBarNextFrameWrapper');
        const offset = previewBar ? Math.round(previewBar.getBoundingClientRect().height) : 0;
        this.style.setProperty('--sticky-atc-offset', offset + 'px');
      }

      setVisible(visible) {
        if (this.visible === visible) return;
        this.visible = visible;
        if (visible) this.updateOffset();
        this.classList.toggle('sticky-atc--visible', visible);
        // Keep the bar out of the tab order and the accessibility tree while
        // the real button is on screen.
        this.inert = !visible;
      }

      onClick(event) {
        event.preventDefault();
        if (this.target.hasAttribute('disabled')) return;
        this.target.click();
      }
    }
  );
}
